import { useTranslation } from 'react-i18next';

import { Info, VFlex, VStack, VTable } from '@/package';
import type { FrameGraphData, ObjectTypeData } from '@/type';

const ModelingDataSummary = ({
  frameGraphData,
  selectedLabel,
  toggleLabel,
  filterableLabel,
}: {
  frameGraphData: FrameGraphData;
  selectedLabel: string[];
  toggleLabel: (label: string) => void;
  filterableLabel: string[];
}) => {
  const { t } = useTranslation();

  // TODO: 하드코딩 제거
  const renderedTableData = ['vehicle', 'pedestrian', 'cyclist'].map(
    labelName => ({
      checked: selectedLabel.includes(labelName),
      id: labelName,
      objectType: labelName,
      total: (
        frameGraphData[labelName as keyof FrameGraphData] as ObjectTypeData
      ).total,
      ap: (frameGraphData[labelName as keyof FrameGraphData] as ObjectTypeData)
        .ap,
      precision: (
        frameGraphData[labelName as keyof FrameGraphData] as ObjectTypeData
      ).max_f1_precision,
      recall: (
        frameGraphData[labelName as keyof FrameGraphData] as ObjectTypeData
      ).recall,
      f1: (frameGraphData[labelName as keyof FrameGraphData] as ObjectTypeData)
        .f1,
      tp: (frameGraphData[labelName as keyof FrameGraphData] as ObjectTypeData)
        .tp,
      fp: (frameGraphData[labelName as keyof FrameGraphData] as ObjectTypeData)
        .fp,
      fn: (frameGraphData[labelName as keyof FrameGraphData] as ObjectTypeData)
        .fn,
    }),
  );

  const isFilterable = (label: string) => filterableLabel.includes(label);

  return (
    <div className="h-fit w-full ">
      <VStack gap={0.5} className="w-full">
        <div className="text-headline-sm font-semibold">Data Summary</div>

        <VTable
          data={renderedTableData}
          className="h-fit max-h-[288px] overflow-y-auto text-body-md"
          columnList={[
            'checked',
            'objectType',
            'total',
            'ap',
            'precision',
            'recall',
            'f1',
            'tp',
            'fp',
            'fn',
          ]}
          columnStyleMap={{
            checked: {
              className: 'w-[49px] text-body-md',
            },
            objectType: {
              className: 'w-[88px] text-body-md',
            },
          }}
          columnRenderer={{
            checked: () => (
              <div className="w-full text-right text-body-md">Graph</div>
            ),
            ap: () => <div className="w-full text-right text-body-md">AP</div>,
            f1: () => <div className="w-full text-right text-body-md">F1</div>,
            tp: () => <div className="w-full text-right text-body-md">TP</div>,
            fp: () => <div className="w-full text-right text-body-md">FP</div>,
            fn: () => <div className="w-full text-right text-body-md">FN</div>,
            precision: () => (
              <div className="w-full text-right text-body-md">Precision</div>
            ),
            recall: () => (
              <div className="w-full text-right text-body-md">Recall</div>
            ),
            total: () => (
              <div className="w-full text-right text-body-md">Total</div>
            ),
          }}
          cellRenderer={{
            objectType: data => (
              <div className="text-body-md font-semibold">{data}</div>
            ),
            total: (data, _, row) => (
              <div className="w-full text-right text-body-md">
                {isFilterable(row.objectType) ? Number(data) : '-'}
              </div>
            ),
            ap: (data, _, row) => (
              <div className="w-full text-right text-body-md">
                {isFilterable(row.objectType) ? Number(data).toFixed(4) : '-'}
              </div>
            ),
            precision: (data, _, row) => (
              <div className="w-full text-right text-body-md">
                {isFilterable(row.objectType) ? Number(data).toFixed(4) : '-'}
              </div>
            ),
            recall: (data, _, row) => (
              <div className="w-full text-right text-body-md">
                {isFilterable(row.objectType) ? Number(data).toFixed(4) : '-'}
              </div>
            ),
            f1: (data, _, row) => (
              <div className="w-full text-right text-body-md">
                {isFilterable(row.objectType) ? Number(data).toFixed(4) : '-'}
              </div>
            ),
            tp: (data, _, row) => (
              <div className="w-full text-right text-body-md">
                {isFilterable(row.objectType) ? Number(data) : '-'}
              </div>
            ),
            fp: (data, _, row) => (
              <div className="w-full text-right text-body-md">
                {isFilterable(row.objectType) ? Number(data) : '-'}
              </div>
            ),
            fn: (data, _, row) => (
              <div className="w-full text-right text-body-md">
                {isFilterable(row.objectType) ? Number(data) : '-'}
              </div>
            ),
          }}
          setRowChecked={row => selectedLabel.includes(row.objectType)}
          setRowSelected={row => isFilterable(row.objectType)}
          onRowClick={data => {
            if (isFilterable(data._target.row.objectType)) {
              toggleLabel(data._target.row.objectType);
            }
          }}
          onRowCheckedChange={row => {
            toggleLabel(row.objectType);
          }}
        />

        <VFlex gap={0.25} items="center" className="px-[18px]">
          <Info className="size-4 text-info-500" />
          <div className="text-body-md font-semibold text-info-500">
            {t('modeling.dataSummaryInfoTextMessage.dungeon')}
          </div>
        </VFlex>
      </VStack>
    </div>
  );
};

export default ModelingDataSummary;
